/**
 * Hook that loads the minimum player identity fields the contract
 * drawer / mobile page header needs: display name, nationality,
 * primary position, age, and squad tier (derived client-side from
 * `isAcademy` + `isOnLoan`). SE-4333 items D + F.
 *
 * Why a dedicated, narrow query rather than reusing
 * `PlayerDetailGetPlayer`: that query pulls ~30 fields including
 * stat-heavy payloads (bioData, seasonProviderMetrics, scoutReports)
 * that the drawer header does not display. Fetching those every time
 * the drawer opens would waste the round-trip and inflate the Apollo
 * cache entry per-player. The `ContractHeaderGetPlayer` query
 * requests exactly the fields surfaced in the metadata line
 * ("Nationality · Squad · Position · Age") and nothing more.
 *
 * The tier string the drawer / mobile page renders is derived the
 * same way `ContractsPlayerListContainer.normaliseRows` derives the
 * row-level `tier`: prefer `isOnLoan` → "Loanee" (SE-4586), else
 * `isAcademy` → "Academy", else "First Team". Keeping the derivation
 * here means the header shows the same tier label the contracts
 * table shows for that player.
 *
 * SE-4333 item D (second pass) — the `bioData` sub-selection MUST
 * include `ggPlayerKey` alongside `age`, even though we never render
 * ggPlayerKey. Apollo's `resolveDataId` keyer in
 * `providers/ApolloClient.tsx` returns `["ggPlayerKey"]` as the
 * keyFields for any `PlayerBioData` object. Omitting it makes Apollo
 * raise `Missing field 'ggPlayerKey' while extracting keyFields from
 * PlayerBioData`, which rejects the whole response — `data.getPlayer`
 * resolves to `null` and the drawer metadata row blanks out even
 * though the backend returned real age data. This was the dev-only
 * symptom first observed on bundle bef3cb59.
 * @module features/contracts/hooks/useContractPlayerIdentity
 */

import { gql, useQuery } from "@apollo/client";
import { useEffect, useMemo } from "react";

/** GraphQL document for the ContractHeaderGetPlayer query. */
export const CONTRACT_HEADER_GET_PLAYER_QUERY = gql`
  query ContractHeaderGetPlayer($id: ID!) {
    getPlayer(id: $id) {
      id
      name
      isAcademy
      isOnLoan
      nationality {
        id
        name
      }
      positions {
        id
        abbreviation
      }
      bioData {
        ggPlayerKey
        age
      }
    }
  }
`;

/**
 * Response shape. Kept minimal and narrowly typed — do not add fields
 * here without also adding them to the query above.
 */
interface ContractHeaderGetPlayerResponse {
  readonly getPlayer: {
    readonly id: string;
    readonly name: string;
    readonly isAcademy?: boolean | null;
    readonly isOnLoan?: boolean | null;
    readonly nationality?: {
      readonly id: string;
      readonly name: string;
    } | null;
    readonly positions?:
      | readonly {
          readonly id: string;
          readonly abbreviation?: string | null;
        }[]
      | null;
    readonly bioData?: {
      /**
       * Not rendered — selected only to satisfy Apollo's
       * PlayerBioData cache keyer (keyFields: `["ggPlayerKey"]`).
       * See the GraphQL query comment above.
       */
      readonly ggPlayerKey: string;
      readonly age?: number | null;
    } | null;
  } | null;
}

/**
 *
 */
interface ContractHeaderGetPlayerVariables {
  readonly id: string;
}

/** Resolved identity payload exposed to the drawer / mobile page. */
export interface ContractPlayerIdentity {
  /** Display name — knownName when set, else "firstName lastName". */
  readonly playerName: string | null;
  /**
   * Metadata line below the name, e.g.
   * "Portugal · First Team · CAM · 31 Years". Empty string when no
   * field is resolved yet (the header already handles empty strings
   * gracefully by hiding the metadata row).
   */
  readonly metadataLine: string;
  /** Whether the identity query is currently in flight. */
  readonly loading: boolean;
}

/**
 * Normalises the backend-canonical `Player.name` (SE-5469) for the
 * contract drawer / mobile-page header. `name` already carries the
 * fully-assembled display name — including particles like "van"/"de"
 * and partial-`knownName` cases (e.g. Myles Anthony Lewis-Skelly) that
 * the old client-side firstName/knownName/lastName assembly used to
 * collapse (SE-4770).
 *
 * An empty/whitespace name normalises back to `null` so the existing
 * "no name resolved yet" caller contract is preserved.
 * @param name - The backend `Player.name` value
 * @returns The trimmed display name, or null when it is empty
 */
function derivePlayerName(name: string | null | undefined): string | null {
  const trimmed = name?.trim();
  return trimmed ? trimmed : null;
}

/**
 * Derives the tier string ("First Team" / "Academy" / "Loanee")
 * shown in the metadata line. Keeps the derivation in parity with
 * `ContractsPlayerListContainer.normaliseRows`:
 *
 * - `isOnLoan === true` → "Loanee" (SE-4586)
 * - `isAcademy === true` → "Academy"
 * - otherwise → "First Team"
 * @param input - Subset of Player booleans used for tier derivation
 * @param input.isAcademy - Academy flag from the Player node
 * @param input.isOnLoan - Loan flag from the Player node
 * @returns The tier label string
 */
function deriveTier(input: {
  readonly isAcademy?: boolean | null;
  readonly isOnLoan?: boolean | null;
}): string {
  if (input.isOnLoan === true) return "Loanee";
  if (input.isAcademy === true) return "Academy";
  return "First Team";
}

/**
 * Joins the four metadata fields with the " · " separator used in
 * the prototype. Skips fields that are empty so the line never has
 * stray separators ("Portugal ·  · CAM · 31 Years").
 * @param parts - Ordered metadata parts to join
 * @returns The joined metadata line or empty string when every part
 *   is empty
 */
function buildMetadataLine(parts: readonly string[]): string {
  return parts.filter(p => p.length > 0).join(" · ");
}

/**
 * Loads the minimal identity payload used by the contract drawer and
 * mobile full-page header. Skips the network query when `playerId`
 * is null so closed drawers don't issue a trailing request.
 * @param playerId - Player UUID to look up, or null when the header
 *   has nothing to resolve yet
 * @returns `{ playerName, metadataLine, loading }` — strings are
 *   empty when identity is still loading or unresolved
 */
export function useContractPlayerIdentity(
  playerId: string | null
): ContractPlayerIdentity {
  const { data, loading, error } = useQuery<
    ContractHeaderGetPlayerResponse,
    ContractHeaderGetPlayerVariables
  >(CONTRACT_HEADER_GET_PLAYER_QUERY, {
    variables: playerId ? { id: playerId } : undefined,
    skip: !playerId,
    // SE-4333 item D — use cache-and-network (not cache-first) so a
    // stale cached `null` response for this player (e.g. hit during
    // a deep-link before the org roster populated the normalized
    // cache) can't permanently blank the drawer header. cache-and-
    // network still returns the cached value immediately for the
    // instant render; it also re-issues the network request so a
    // second render has real data.
    fetchPolicy: "cache-and-network",
  });

  // SE-4333 item D — surface identity query failures so they're not
  // silently swallowed into an empty metadata row. console.error is a
  // placeholder until Sentry is wired up for this feature; the event
  // name ("useContractPlayerIdentity") is load-bearing so a grep in
  // CloudWatch / Sentry narrows straight to the hook.
  useEffect(() => {
    if (error) {
      console.error("useContractPlayerIdentity identity query failed", error);
    }
  }, [error]);

  return useMemo<ContractPlayerIdentity>(() => {
    const player = data?.getPlayer;
    if (!player) {
      return { playerName: null, metadataLine: "", loading };
    }

    const playerName = derivePlayerName(player.name);

    const nationality = player.nationality?.name ?? "";
    const tier = deriveTier({
      isAcademy: player.isAcademy,
      isOnLoan: player.isOnLoan,
    });
    // Primary position = first position's abbreviation. The backend
    // sorts `positions` by primacy, so `[0]` is the canonical choice.
    const position = player.positions?.[0]?.abbreviation ?? "";
    const age = player.bioData?.age;
    const ageText = typeof age === "number" ? `${age} Years` : "";

    const metadataLine = buildMetadataLine([
      nationality,
      tier,
      position,
      ageText,
    ]);

    return { playerName, metadataLine, loading };
  }, [data, loading]);
}
